import React, { useState, useMemo } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ArrowLeft, BookOpen, Plus, Pencil, ArrowRight, Link2, Unlink, FileText, Award,
  Zap, Calculator, Copy, Trash2, Search, X, SlidersHorizontal, RotateCcw,
  Sparkles, Globe, ShieldCheck, ChevronRight, Trophy
} from 'lucide-react'

import { resolveBackNavigation } from '@/lib/utils'
import { MarkingRulesManager } from '@/components/shared/MarkingRulesManager'

interface SyllabusChapterOption {
  id: number
  syllabus_chapter_id?: number
  name: string
}

interface SyllabusSubjectOption {
  id: number
  syllabus_subject_id?: number
  name: string
  chapters: SyllabusChapterOption[]
}

interface MappedSyllabus {
  id: number
  name: string
  version: number
  description: string | null
  subjects_count: number
  chapters_count: number
  subjects?: SyllabusSubjectOption[]
}

interface UnmappedSyllabus {
  id: number
  name: string
  version: number
  description: string | null
  current_exam_name: string | null
}

interface MappedTest {
  id: number
  title: string
  description: string | null
  test_type: string
  language: string
  status: string
  is_free: boolean
  credit_cost: number
  question_count: number
  total_marks: number
  duration_minutes: number
}

interface UnmappedTest {
  id: number
  title: string
  test_type: string
  language: string
  status: string
  current_exam_name: string | null
}

interface Exam {
  id: number
  name: string
  code: string
  year: number | null
  is_active: boolean
  description?: string | null
  category?: string | null
  calculator_enabled?: boolean
  allow_language_switch?: boolean
  primary_language?: string
  score_display_mode?: 'raw_score' | 'percentage' | 'percentile' | 'nta_score'
}

interface MarkingRule {
  id: number
  name: string
  question_type: string
  scoring_strategy: string
  scoring_params: Record<string, any>
  marked_for_review_rule?: string
  description?: string
}

interface BlueprintSection {
  id?: number
  name: string
  sort_order: number
  total_questions: number
  max_attempts?: number | null
  duration_minutes?: number | null
  subject_hint?: string | null
  exam_marking_rule_id?: number | null
  marking_rule?: { id: number; name: string; strategy: string } | null
  syllabus_id?: number | null
  subject_id?: number | null
  syllabus_ids?: number[]
  subject_ids?: number[]
  chapter_ids?: number[]
  subject?: { id: number; name: string } | null
  scope_deferred: boolean
  deferred_scope_type?: 'chapter' | 'subject' | null
  _destroy?: boolean
}

interface ExamTestTypeData {
  id: number
  name: string
  test_type_key: string
  total_questions: number
  max_attempts?: number | null
  duration_minutes: number | null
  timer_type?: string
  positive_marks: number
  negative_marks: number
  leaderboard_eligible?: boolean
  exam_marking_rule_id?: number | null
  marking_rule?: {
    id: number
    name: string
    scoring_strategy: string
    scoring_params: any
  } | null
  description: string | null
  instructions_html?: string | null
  sort_order: number
  sections: BlueprintSection[]
}

interface Props {
  exam: Exam
  marking_rules: MarkingRule[]
  test_types?: ExamTestTypeData[]
  mapped_syllabi: MappedSyllabus[]
  unmapped_syllabi: UnmappedSyllabus[]
  mapped_tests: MappedTest[]
  unmapped_tests: UnmappedTest[]
}

type TabType = 'blueprints' | 'tests' | 'syllabi' | 'rules' | 'settings'

function formatTestDuration(minutes?: number | null): string {
  if (minutes === null || minutes === undefined || minutes <= 0) {
    return 'Untimed'
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    return `${Math.floor(minutes / 60)}h`
  }
  if (minutes >= 60) {
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
  }
  return `${minutes}m`
}

function BlueprintCard({
  tt,
  examId,
  onDuplicate,
  onDelete,
  onToggleLeaderboard,
}: {
  tt: ExamTestTypeData
  examId: number
  onDuplicate: (tt: ExamTestTypeData) => void
  onDelete: (tt: ExamTestTypeData) => void
  onToggleLeaderboard?: (tt: ExamTestTypeData) => void
}) {
  const [showInstructions, setShowInstructions] = useState(false)
  const [showAllSections, setShowAllSections] = useState(false)
  const sections = tt.sections || []
  const visibleSections = showAllSections ? sections : sections.slice(0, 2)

  return (
    <Card className="relative overflow-hidden border-border/80 bg-card hover:border-primary/40 transition-all flex flex-col justify-between rounded-2xl shadow-glass-xs">
      <div>
        <CardHeader className="p-3.5 sm:p-5 pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2 flex-1 min-w-0">
              {/* Title & Action Toolbar */}
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold text-sm sm:text-base text-foreground leading-snug break-words">
                  {tt.name}
                </h3>

                {/* Quick Action Icons */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground rounded-lg"
                    title="Duplicate Blueprint"
                    onClick={() => onDuplicate(tt)}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Link
                    href={`/admin/exams/${examId}/exam_test_types/${tt.id}/edit`}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title="Edit Blueprint"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:bg-destructive/10 rounded-lg"
                    title="Delete Blueprint"
                    onClick={() => onDelete(tt)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Badges Strip */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="font-mono text-[10px] uppercase font-bold px-2 py-0.5 bg-muted/40 shrink-0">
                  {tt.test_type_key.replace(/_/g, ' ')}
                </Badge>
                <Badge variant="outline" className="text-[10px] uppercase font-medium bg-muted/40 text-muted-foreground border-border/60 shrink-0 whitespace-nowrap">
                  ⏱️ {tt.timer_type === 'none' || tt.duration_minutes == null || tt.test_type_key === 'practice'
                    ? 'Untimed'
                    : tt.timer_type === 'sectional'
                    ? 'Sectional Timer'
                    : 'Global Timer'}
                </Badge>
                {tt.marking_rule && (
                  <Badge variant="outline" className="text-[10px] font-semibold bg-primary/15 text-primary border-primary/25 shrink-0 whitespace-nowrap">
                    ⚖️ {tt.marking_rule.name}
                  </Badge>
                )}
                {onToggleLeaderboard ? (
                  <button
                    type="button"
                    onClick={() => onToggleLeaderboard(tt)}
                    title="Click to toggle leaderboard eligibility for this blueprint"
                    className="cursor-pointer"
                  >
                    {tt.leaderboard_eligible ? (
                      <Badge variant="outline" className="text-[10px] font-bold bg-secondary/15 text-secondary border-secondary/30 shrink-0 whitespace-nowrap flex items-center gap-1 hover:bg-secondary/25 transition-colors">
                        <Trophy className="h-3 w-3 text-secondary" /> Leaderboard Eligible
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] font-medium bg-muted/40 text-muted-foreground border-border/60 shrink-0 whitespace-nowrap hover:bg-muted/70 transition-colors">
                        Leaderboard Excluded
                      </Badge>
                    )}
                  </button>
                ) : (
                  tt.leaderboard_eligible ? (
                    <Badge variant="outline" className="text-[10px] font-bold bg-secondary/15 text-secondary border-secondary/30 shrink-0 whitespace-nowrap flex items-center gap-1">
                      <Trophy className="h-3 w-3 text-secondary" /> Leaderboard Eligible
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] font-medium bg-muted/40 text-muted-foreground border-border/60 shrink-0 whitespace-nowrap">
                      Leaderboard Excluded
                    </Badge>
                  )
                )}
              </div>

              {tt.description && (
                <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                  {tt.description}
                </p>
              )}

              {/* Collapsible Candidate Instructions */}
              {tt.instructions_html && (
                <div className="pt-0.5">
                  <button
                    type="button"
                    onClick={() => setShowInstructions(!showInstructions)}
                    className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <FileText className="h-3 w-3 shrink-0" />
                    <span>{showInstructions ? 'Hide Candidate Instructions' : 'View Candidate Instructions'}</span>
                  </button>
                  {showInstructions && (
                    <div className="text-[11px] bg-muted/40 border border-border/40 rounded-xl p-2.5 text-muted-foreground mt-1.5 space-y-0.5">
                      <p className="leading-relaxed whitespace-pre-line">
                        {tt.instructions_html.replace(/<[^>]+>/g, ' ').trim()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-3.5 sm:p-5 pt-0 space-y-3">
          {/* Stats Matrix: 2-col on mobile, 4-col on tablet/desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
            <div className="p-2 rounded-xl bg-muted/40 border border-border/40 space-y-0.5">
              <div className="text-[10px] text-muted-foreground font-semibold">Questions</div>
              <div className="font-extrabold text-foreground">
                {tt.total_questions} Qs
                {tt.max_attempts && tt.max_attempts < tt.total_questions && (
                  <span className="text-[9px] font-bold text-primary block">
                    Max {tt.max_attempts}
                  </span>
                )}
              </div>
            </div>

            <div className="p-2 rounded-xl bg-muted/40 border border-border/40 space-y-0.5">
              <div className="text-[10px] text-muted-foreground font-semibold">Duration</div>
              <div className="font-extrabold text-foreground">
                {formatTestDuration(tt.duration_minutes)}
              </div>
            </div>

            <div className="p-2 rounded-xl bg-muted/40 border border-border/40 space-y-0.5">
              <div className="text-[10px] text-muted-foreground font-semibold">Correct</div>
              <div className="font-extrabold text-primary">+{tt.positive_marks}</div>
            </div>

            <div className="p-2 rounded-xl bg-muted/40 border border-border/40 space-y-0.5">
              <div className="text-[10px] text-muted-foreground font-semibold">Penalty</div>
              <div className={`font-extrabold ${tt.negative_marks > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {tt.negative_marks > 0 ? `-${tt.negative_marks}` : 'None'}
              </div>
            </div>
          </div>

          {/* Section Breakdown with Collapsible overflow */}
          {sections.length > 0 ? (
            <div className="w-full pt-2 border-t border-border/50 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Sections ({sections.length})
                </span>
                <span className="text-[10px] text-muted-foreground font-mono">
                  {sections.reduce((sum, s) => sum + (s.total_questions || 0), 0)} Qs in sections
                </span>
              </div>
              <div className="space-y-1">
                {visibleSections.map((sec, i) => (
                  <div key={sec.id || i} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-muted/30 border border-border/30">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-muted-foreground font-mono text-[10px] shrink-0">{i + 1}</span>
                      <span className="font-medium text-foreground truncate">{sec.name}</span>
                      {sec.scope_deferred ? (
                        <span className="text-[9px] uppercase font-bold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.2 rounded shrink-0">
                          {sec.deferred_scope_type === 'chapter' ? 'Pick Chapter' : 'Pick Subject'}
                        </span>
                      ) : (sec.subject ? (
                        <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.2 rounded shrink-0 truncate max-w-[90px]">
                          {sec.subject.name}
                        </span>
                      ) : sec.subject_hint ? (
                        <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.2 rounded shrink-0 truncate max-w-[90px]">
                          {sec.subject_hint}
                        </span>
                      ) : null)}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground shrink-0 font-medium">
                      <span>{sec.total_questions}Q</span>
                      {sec.duration_minutes && <span>{sec.duration_minutes}m</span>}
                    </div>
                  </div>
                ))}
              </div>

              {sections.length > 2 && (
                <button
                  type="button"
                  onClick={() => setShowAllSections(!showAllSections)}
                  className="w-full text-center py-1 text-[11px] font-semibold text-primary hover:underline cursor-pointer"
                >
                  {showAllSections ? 'Show fewer sections' : `+ Show ${sections.length - 2} more sections`}
                </button>
              )}
            </div>
          ) : (
            <div className="w-full pt-2 border-t border-border/50 text-center py-1">
              <span className="text-[10px] text-muted-foreground italic">
                Single section paper (flat structure)
              </span>
            </div>
          )}

          {/* Action Footer */}
          <div className="pt-2 border-t border-border/50 flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              asChild
              className="flex-1 rounded-xl text-xs font-bold h-8 border-border/80 text-foreground hover:bg-muted min-w-0 px-2 sm:px-3"
            >
              <Link href={`/admin/exams/${examId}/exam_test_types/${tt.id}/edit`} className="inline-flex items-center justify-center truncate gap-1.5">
                <Pencil className="h-3 w-3 shrink-0" />
                <span className="hidden sm:inline">Edit Blueprint</span>
                <span className="sm:hidden">Edit</span>
              </Link>
            </Button>
            <Button
              size="sm"
              asChild
              className="flex-1 rounded-xl text-xs font-bold h-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs min-w-0 px-2 sm:px-3"
            >
              <Link href={`/admin/exams/${examId}?tab=tests`} className="inline-flex items-center justify-center truncate gap-1.5">
                <Link2 className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline">View Tests</span>
                <span className="sm:hidden">Tests</span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </div>
    </Card>
  )
}

export default function ExamShow({
  exam,
  marking_rules = [],
  test_types = [],
  mapped_syllabi = [],
  unmapped_syllabi = [],
  mapped_tests = [],
  unmapped_tests = [],
}: Props) {
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const initialParam = searchParams?.get('tab')

  // Resolve active tab with backwards compatibility
  const getInitialTab = (): TabType => {
    if (initialParam === 'curriculum') return 'tests'
    if (initialParam === 'blueprints' || initialParam === 'test_types') return 'blueprints'
    if (initialParam === 'rules') return 'rules'
    if (initialParam === 'tests') return 'tests'
    if (initialParam === 'syllabi') return 'syllabi'
    if (initialParam === 'settings') return 'settings'
    return 'blueprints'
  }

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab())

  // Filter & Search states for Tests Tab
  const [testSearch, setTestSearch] = useState('')
  const [testStatusFilter, setTestStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all')
  const [testPricingFilter, setTestPricingFilter] = useState<'all' | 'free' | 'premium'>('all')

  // Filter & Search states for Blueprints Tab
  const [blueprintSearch, setBlueprintSearch] = useState('')
  const [blueprintTypeFilter, setBlueprintTypeFilter] = useState<string>('all')
  const [blueprintTimerFilter, setBlueprintTimerFilter] = useState<string>('all')
  const [blueprintRuleFilter, setBlueprintRuleFilter] = useState<string>('all')

  // Edit Exam Modal state
  const [editExamModalOpen, setEditExamModalOpen] = useState(false)
  const [editName, setEditName] = useState(exam.name)
  const [editCode, setEditCode] = useState(exam.code)
  const [editYear, setEditYear] = useState<number | ''>(exam.year ?? '')
  const [editIsActive, setEditIsActive] = useState(exam.is_active)

  // Exam Candidate Tools state
  const [calculatorEnabled, setCalculatorEnabled] = useState(exam.calculator_enabled || false)
  const [allowLanguageSwitch, setAllowLanguageSwitch] = useState(exam.allow_language_switch ?? true)
  const [primaryLanguage, setPrimaryLanguage] = useState(exam.primary_language || 'en')
  const [scoreDisplayMode, setScoreDisplayMode] = useState<string>(exam.score_display_mode || 'raw_score')
  const [isSavingSettings, setIsSavingSettings] = useState(false)

  // Map Syllabus Modal state
  const [mapSyllabusModalOpen, setMapSyllabusModalOpen] = useState(false)
  const [selectedSyllabusId, setSelectedSyllabusId] = useState<number | ''>('')

  // Create Syllabus Modal state
  const [createSyllabusModalOpen, setCreateSyllabusModalOpen] = useState(false)
  const [newSyllabusName, setNewSyllabusName] = useState(`${exam.name} Prescribed Syllabus`)
  const [newSyllabusVersion, setNewSyllabusVersion] = useState(1)
  const [newSyllabusDesc, setNewSyllabusDesc] = useState('')

  // Map Test Modal state
  const [mapTestModalOpen, setMapTestModalOpen] = useState(false)
  const [selectedTestId, setSelectedTestId] = useState<number | ''>('')

  // Create Test Modal state
  const [createTestModalOpen, setCreateTestModalOpen] = useState(false)
  const [newTestTitle, setNewTestTitle] = useState(`${exam.name} Full Mock Test 1`)
  const [newTestType, setNewTestType] = useState('full_mock')
  const [newTestLanguage, setNewTestLanguage] = useState('en')
  const [newTestDuration, setNewTestDuration] = useState(180)
  const [newTestIsFree, setNewTestIsFree] = useState(true)
  const [newTestCreditCost, setNewTestCreditCost] = useState(0)

  // Filtered tests logic
  const filteredTests = useMemo(() => {
    return mapped_tests.filter(t => {
      if (testSearch.trim()) {
        const q = testSearch.toLowerCase()
        const matches =
          t.title.toLowerCase().includes(q) ||
          (t.description && t.description.toLowerCase().includes(q)) ||
          t.test_type.toLowerCase().includes(q)
        if (!matches) return false
      }

      if (testStatusFilter !== 'all') {
        if (t.status !== testStatusFilter) return false
      }

      if (testPricingFilter === 'free' && !t.is_free) return false
      if (testPricingFilter === 'premium' && t.is_free) return false

      return true
    })
  }, [mapped_tests, testSearch, testStatusFilter, testPricingFilter])

  // Filtered blueprints logic
  const availableBlueprintTypes = useMemo(() => {
    return Array.from(new Set(test_types.map(tt => tt.test_type_key))).filter(Boolean)
  }, [test_types])

  const availableTimerTypes = useMemo(() => {
    return Array.from(new Set(test_types.map(tt => {
      if (tt.timer_type === 'none' || tt.duration_minutes == null || tt.test_type_key === 'practice') {
        return 'none'
      }
      return tt.timer_type || 'global'
    }))).filter(Boolean)
  }, [test_types])

  const availableBlueprintRules = useMemo(() => {
    const map = new Map<number, string>()
    test_types.forEach(tt => {
      if (tt.marking_rule) {
        map.set(tt.marking_rule.id, tt.marking_rule.name)
      }
    })
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [test_types])

  const filteredBlueprints = useMemo(() => {
    return test_types.filter(tt => {
      if (blueprintSearch.trim()) {
        const q = blueprintSearch.toLowerCase()
        const matches =
          tt.name.toLowerCase().includes(q) ||
          (tt.description && tt.description.toLowerCase().includes(q)) ||
          tt.test_type_key.toLowerCase().includes(q) ||
          (tt.sections && tt.sections.some(s => s.name.toLowerCase().includes(q) || (s.subject_hint && s.subject_hint.toLowerCase().includes(q))))
        if (!matches) return false
      }

      if (blueprintTypeFilter !== 'all') {
        if (tt.test_type_key !== blueprintTypeFilter) return false
      }

      if (blueprintTimerFilter !== 'all') {
        const timerCategory =
          tt.timer_type === 'none' || tt.duration_minutes == null || tt.test_type_key === 'practice'
            ? 'none'
            : tt.timer_type === 'sectional'
            ? 'sectional'
            : 'global'
        if (timerCategory !== blueprintTimerFilter) return false
      }

      if (blueprintRuleFilter !== 'all') {
        if (String(tt.marking_rule?.id) !== String(blueprintRuleFilter)) return false
      }

      return true
    })
  }, [test_types, blueprintSearch, blueprintTypeFilter, blueprintTimerFilter, blueprintRuleFilter])

  const hasActiveBlueprintFilters =
    Boolean(blueprintSearch.trim()) ||
    blueprintTypeFilter !== 'all' ||
    blueprintTimerFilter !== 'all' ||
    blueprintRuleFilter !== 'all'

  const handleResetBlueprintFilters = () => {
    setBlueprintSearch('')
    setBlueprintTypeFilter('all')
    setBlueprintTimerFilter('all')
    setBlueprintRuleFilter('all')
  }

  const publishedTestCount = useMemo(() => mapped_tests.filter(t => t.status === 'published').length, [mapped_tests])
  const totalSubjects = useMemo(() => mapped_syllabi.reduce((acc, s) => acc + s.subjects_count, 0), [mapped_syllabi])
  const totalChapters = useMemo(() => mapped_syllabi.reduce((acc, s) => acc + s.chapters_count, 0), [mapped_syllabi])
  const { href: backHref, label: backLabel } = resolveBackNavigation('/admin/exams', 'Back to Exams')

  const handleUpdateExam = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch(`/admin/exams/${exam.id}`, {
      exam: {
        name: editName,
        code: editCode,
        year: editYear === '' ? null : Number(editYear),
        is_active: editIsActive,
        calculator_enabled: calculatorEnabled,
        allow_language_switch: allowLanguageSwitch,
        primary_language: primaryLanguage,
        score_display_mode: scoreDisplayMode
      }
    }, {
      onSuccess: () => setEditExamModalOpen(false)
    })
  }

  const handleSaveSettings = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSavingSettings(true)
    router.patch(`/admin/exams/${exam.id}`, {
      exam: {
        calculator_enabled: calculatorEnabled,
        allow_language_switch: allowLanguageSwitch,
        primary_language: primaryLanguage,
        score_display_mode: scoreDisplayMode
      }
    }, {
      onFinish: () => setIsSavingSettings(false)
    })
  }

  const handleToggleLeaderboard = (tt: ExamTestTypeData) => {
    router.patch(`/admin/exams/${exam.id}/exam_test_types/${tt.id}`, {
      exam_test_type: {
        leaderboard_eligible: !tt.leaderboard_eligible
      }
    }, {
      preserveScroll: true
    })
  }

  const handleMapSyllabus = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedSyllabusId) return

    router.post(`/admin/exams/${exam.id}/map_syllabus`, {
      syllabus_id: selectedSyllabusId
    }, {
      onSuccess: () => {
        setMapSyllabusModalOpen(false)
        setSelectedSyllabusId('')
      }
    })
  }

  const handleUnmapSyllabus = (syllabusId: number, syllabusName: string) => {
    if (!confirm(`Unmap syllabus "${syllabusName}" from ${exam.name}?`)) return
    router.delete(`/admin/exams/${exam.id}/unmap_syllabus`, {
      data: { syllabus_id: syllabusId }
    })
  }

  const handleCreateSyllabus = (e: React.FormEvent) => {
    e.preventDefault()
    router.post('/admin/syllabi', {
      syllabus: {
        name: newSyllabusName,
        version: newSyllabusVersion,
        description: newSyllabusDesc || null,
        exam_id: exam.id
      },
      return_to: `/admin/exams/${exam.id}?tab=syllabi`
    })
  }

  const handleMapTest = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTestId) return

    router.post(`/admin/exams/${exam.id}/map_test`, {
      test_id: selectedTestId
    }, {
      onSuccess: () => {
        setMapTestModalOpen(false)
        setSelectedTestId('')
      }
    })
  }

  const handleUnmapTest = (testId: number, testTitle: string) => {
    if (!confirm(`Unmap test "${testTitle}" from ${exam.name}?`)) return
    router.delete(`/admin/exams/${exam.id}/unmap_test`, {
      data: { test_id: testId }
    })
  }

  const handleCreateTest = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTestTitle.trim()) return

    router.post(`/admin/exams/${exam.id}/create_test`, {
      test: {
        title: newTestTitle.trim(),
        test_type: newTestType,
        language: newTestLanguage,
        duration_minutes: newTestDuration,
        is_free: newTestIsFree,
        credit_cost: newTestIsFree ? 0 : newTestCreditCost
      }
    })
  }

  const handleDuplicateBlueprint = (tt: ExamTestTypeData) => {
    router.post(`/admin/exams/${exam.id}/exam_test_types/${tt.id}/duplicate`, {}, {
      onSuccess: () => {}
    })
  }

  const handleDeleteBlueprint = (tt: ExamTestTypeData) => {
    if (!confirm(`Delete blueprint "${tt.name}"? This cannot be undone.`)) return
    router.delete(`/admin/exams/${exam.id}/exam_test_types/${tt.id}`, {
      onSuccess: () => {}
    })
  }

  const tabsConfig = [
    { id: 'blueprints' as TabType, label: 'Test Blueprints', shortLabel: 'Blueprints', icon: Zap, count: test_types.length },
    { id: 'tests' as TabType, label: 'Tests', shortLabel: 'Tests', icon: FileText, count: mapped_tests.length },
    { id: 'syllabi' as TabType, label: 'Curriculum & Syllabi', shortLabel: 'Curriculum', icon: BookOpen, count: mapped_syllabi.length },
    { id: 'rules' as TabType, label: 'Marking Schemes', shortLabel: 'Marking', icon: Award, count: marking_rules.length },
    { id: 'settings' as TabType, label: 'Candidate Tools & Settings', shortLabel: 'Settings', icon: SlidersHorizontal }
  ]

  return (
    <AppShell>
      <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto w-full min-w-0 pb-12">
        {/* Navigation Breadcrumbs */}
        <div className="flex items-center gap-1.5 min-w-0">
          <Button variant="ghost" size="sm" asChild className="h-8 px-2 text-xs gap-1.5 text-muted-foreground hover:text-foreground shrink-0">
            <Link href={backHref}>
              <ArrowLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{backLabel}</span><span className="sm:hidden">Exams</span>
            </Link>
          </Button>
          <span className="text-muted-foreground/40 shrink-0">/</span>
          <Badge variant="outline" className="text-[10px] sm:text-xs font-mono font-bold px-2 py-0.5 rounded-lg bg-muted text-foreground border-border truncate max-w-[140px] sm:max-w-none">
            #{exam.code}
          </Badge>
        </div>

        {/* Exam Hero Command Center */}
        <Card className="rounded-2xl border-border/80 bg-card shadow-glass-xs overflow-hidden">
          <CardHeader className="p-3.5 sm:p-5 pb-3 sm:pb-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
              <div className="space-y-2 max-w-3xl flex-1">
                {/* Badges Strip & Edit Action */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="font-mono text-[10px] uppercase px-2 py-0.5 border-border bg-muted/60 font-bold shrink-0">
                      Code: {exam.code}
                    </Badge>

                    {exam.year && (
                      <Badge className="bg-primary/15 text-primary border border-primary/30 font-bold text-[10px] px-2 py-0.5 rounded-full shrink-0">
                        Year {exam.year}
                      </Badge>
                    )}

                    <Badge className={`font-bold text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${
                      exam.is_active
                        ? 'bg-secondary/15 text-secondary border-secondary/30'
                        : 'bg-muted text-muted-foreground border-border'
                    }`}>
                      {exam.is_active ? '● Active' : '○ Inactive'}
                    </Badge>

                    {exam.calculator_enabled && (
                      <Badge variant="outline" className="text-[10px] font-semibold border-secondary/40 text-secondary bg-secondary/10 flex items-center gap-1 shrink-0">
                        <Calculator className="h-3 w-3" />
                        <span>Calculator</span>
                      </Badge>
                    )}
                  </div>

                  {/* Edit Exam Button (Icon-only) */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setEditExamModalOpen(true)}
                    className="rounded-lg border-border/80 text-muted-foreground hover:text-foreground hover:bg-muted h-7 w-7 shrink-0 shadow-2xs"
                    title="Edit Exam"
                    aria-label="Edit Exam"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <div>
                  <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold font-serif text-foreground tracking-tight">
                    {exam.name}
                  </h1>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                    {exam.description || `Comprehensive examination master settings, syllabus distribution, blueprints, and scoring configuration.`}
                  </p>
                </div>
              </div>

              {/* Quick Action Pills */}
              {unmapped_tests.length > 0 && (
                <div className="flex items-center gap-2 shrink-0 pt-1 lg:pt-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setMapTestModalOpen(true)}
                    className="text-xs font-bold rounded-xl gap-1.5 border-border/80 h-8 px-3"
                  >
                    <Link2 className="h-3.5 w-3.5 text-secondary" /> Map Test
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>

          {/* Interactive Metric Strip */}
          <CardContent className="p-3 sm:p-5 pt-0 border-t border-border/50">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 pt-3">
              <button
                type="button"
                onClick={() => setActiveTab('blueprints')}
                className={`p-2.5 sm:p-3 rounded-xl border text-left transition-all cursor-pointer select-none ${
                  activeTab === 'blueprints'
                    ? 'bg-primary/10 border-primary/40 shadow-2xs ring-1 ring-primary/30'
                    : 'bg-muted/30 border-border/60 hover:bg-muted/60 hover:border-primary/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1 truncate">
                    <Zap className="h-3 w-3 text-primary shrink-0" /> Blueprints
                  </span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-60 shrink-0" />
                </div>
                <div className="text-base sm:text-xl font-bold text-foreground mt-0.5 sm:mt-1">
                  {test_types.length}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  Preset test configs
                </div>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('tests')}
                className={`p-2.5 sm:p-3 rounded-xl border text-left transition-all cursor-pointer select-none ${
                  activeTab === 'tests'
                    ? 'bg-secondary/10 border-secondary/40 shadow-2xs ring-1 ring-secondary/30'
                    : 'bg-muted/30 border-border/60 hover:bg-muted/60 hover:border-secondary/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1 truncate">
                    <FileText className="h-3 w-3 text-secondary shrink-0" /> Tests
                  </span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-60 shrink-0" />
                </div>
                <div className="text-base sm:text-xl font-bold text-foreground mt-0.5 sm:mt-1">
                  {mapped_tests.length}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {publishedTestCount} published
                </div>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('syllabi')}
                className={`p-2.5 sm:p-3 rounded-xl border text-left transition-all cursor-pointer select-none ${
                  activeTab === 'syllabi'
                    ? 'bg-tertiary/10 border-tertiary/40 shadow-2xs ring-1 ring-tertiary/30'
                    : 'bg-muted/30 border-border/60 hover:bg-muted/60 hover:border-tertiary/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1 truncate">
                    <BookOpen className="h-3 w-3 text-tertiary shrink-0" /> Curriculum
                  </span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-60 shrink-0" />
                </div>
                <div className="text-base sm:text-xl font-bold text-foreground mt-0.5 sm:mt-1">
                  {mapped_syllabi.length} <span className="text-[10px] font-normal text-muted-foreground">Syllabi</span>
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {totalSubjects} sub • {totalChapters} chap
                </div>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('rules')}
                className={`p-2.5 sm:p-3 rounded-xl border text-left transition-all cursor-pointer select-none ${
                  activeTab === 'rules'
                    ? 'bg-primary/10 border-primary/40 shadow-2xs ring-1 ring-primary/30'
                    : 'bg-muted/30 border-border/60 hover:bg-muted/60 hover:border-primary/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1 truncate">
                    <Award className="h-3 w-3 text-primary shrink-0" /> Marking
                  </span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-60 shrink-0" />
                </div>
                <div className="text-base sm:text-xl font-bold text-foreground mt-0.5 sm:mt-1">
                  {marking_rules.length}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  Scoring rules
                </div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation Controls */}
        <div className="flex items-center gap-1.5 border-b border-border/80 pb-2 overflow-x-auto no-scrollbar">
          {tabsConfig.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer select-none ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-2xs'
                    : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent hover:border-border/60'
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
                {tab.count !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-bold ${
                    isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* ─── TAB 1: TEST BLUEPRINTS ─── */}
        {activeTab === 'blueprints' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-card border border-border/80 rounded-2xl p-3.5 sm:p-4 shadow-glass-xs space-y-3">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-1.5 truncate">
                    <Zap className="h-4 w-4 text-primary shrink-0" />
                    <span>Test Blueprints</span>
                  </h2>
                  <Badge variant="outline" className="text-[10px] font-bold bg-muted/60 text-muted-foreground border-border shrink-0 hidden sm:inline-flex">
                    {test_types.length} Presets
                  </Badge>
                </div>

                <Button
                  asChild
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold rounded-xl gap-1 h-8 px-2.5 sm:px-3 shrink-0 shadow-2xs"
                >
                  <Link href={`/admin/exams/${exam.id}/exam_test_types/new`}>
                    <Plus className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Add Blueprint</span>
                    <span className="sm:hidden">Add</span>
                  </Link>
                </Button>
              </div>

              {/* Blueprint Search & Filters Toolbar */}
              {test_types.length > 0 && (
                <div className="space-y-2.5 pt-2.5 border-t border-border/50">
                  <div className="relative w-full">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      type="text"
                      placeholder="Search blueprints by name, section..."
                      value={blueprintSearch}
                      onChange={(e) => setBlueprintSearch(e.target.value)}
                      className="pl-8 pr-8 h-8 rounded-xl bg-background border-border/80 text-xs"
                    />
                    {blueprintSearch && (
                      <button
                        type="button"
                        onClick={() => setBlueprintSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                        title="Clear search"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  {/* Filter Pills - scrollable on mobile */}
                  <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
                    <div className="flex items-center p-0.5 bg-muted/40 rounded-xl border border-border/60 shrink-0">
                      <button
                        type="button"
                        onClick={() => setBlueprintTypeFilter('all')}
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                          blueprintTypeFilter === 'all'
                            ? 'bg-card text-foreground shadow-2xs border border-border/60'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        All Types
                      </button>
                      {availableBlueprintTypes.map(key => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setBlueprintTypeFilter(key)}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                            blueprintTypeFilter === key
                              ? 'bg-card text-foreground shadow-2xs border border-border/60'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {key.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>

                    {availableTimerTypes.length > 1 && (
                      <div className="flex items-center p-0.5 bg-muted/40 rounded-xl border border-border/60 shrink-0">
                        <button
                          type="button"
                          onClick={() => setBlueprintTimerFilter('all')}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                            blueprintTimerFilter === 'all'
                              ? 'bg-card text-foreground shadow-2xs border border-border/60'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          All Timers
                        </button>
                        {availableTimerTypes.map(timer => (
                          <button
                            key={timer}
                            type="button"
                            onClick={() => setBlueprintTimerFilter(timer)}
                            className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                              blueprintTimerFilter === timer
                                ? 'bg-card text-foreground shadow-2xs border border-border/60'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {timer === 'none' ? 'Untimed' : timer === 'sectional' ? 'Sectional' : 'Global'}
                          </button>
                        ))}
                      </div>
                    )}

                    {availableBlueprintRules.length > 1 && (
                      <select
                        value={blueprintRuleFilter}
                        onChange={(e) => setBlueprintRuleFilter(e.target.value)}
                        aria-label="Filter by Marking Scheme"
                        className="h-7 rounded-xl bg-muted/40 border border-border/60 px-2 text-[10px] font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shrink-0"
                      >
                        <option value="all">All Schemes</option>
                        {availableBlueprintRules.map(r => (
                          <option key={r.id} value={r.id}>
                            ⚖️ {r.name}
                          </option>
                        ))}
                      </select>
                    )}

                    {hasActiveBlueprintFilters && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleResetBlueprintFilters}
                        className="h-7 px-2 text-[10px] text-muted-foreground hover:text-foreground rounded-xl shrink-0"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" /> Reset
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Filter Result Counter */}
            {test_types.length > 0 && hasActiveBlueprintFilters && (
              <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <span>
                  Showing <strong className="text-foreground">{filteredBlueprints.length}</strong> of{' '}
                  <strong className="text-foreground">{test_types.length}</strong> blueprints
                </span>
                <button
                  type="button"
                  onClick={handleResetBlueprintFilters}
                  className="hover:text-foreground text-primary underline text-xs cursor-pointer font-medium"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {test_types.length === 0 ? (
              <Card className="border-dashed border-2 border-border/80 bg-card rounded-2xl">
                <CardContent className="py-12 text-center space-y-4">
                  <div className="p-3.5 rounded-full bg-primary/10 text-primary w-14 h-14 mx-auto flex items-center justify-center">
                    <Zap className="h-7 w-7" />
                  </div>
                  <div className="space-y-1 max-w-md mx-auto">
                    <h4 className="font-bold text-base text-foreground">No Test Blueprints Configured</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Add test blueprints (e.g. Prelims Full Mock, Quantitative Sectional, Chapter Quiz) so test generation wizards automatically apply this exam's exact rules.
                    </p>
                  </div>
                  <Button asChild className="text-xs font-bold rounded-xl gap-1.5">
                    <Link href={`/admin/exams/${exam.id}/exam_test_types/new`}>
                      <Plus className="h-4 w-4" /> Create First Blueprint
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : filteredBlueprints.length === 0 ? (
              <Card className="border-border/80 bg-card rounded-2xl">
                <CardContent className="py-12 text-center text-muted-foreground space-y-3">
                  <Zap className="h-10 w-10 mx-auto text-muted-foreground/50" />
                  <p className="font-bold text-base text-foreground">
                    No blueprints matching current filters.
                  </p>
                  <p className="text-xs max-w-sm mx-auto">
                    Try adjusting your search terms or filter pills to view available blueprints.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleResetBlueprintFilters}
                    className="text-xs rounded-xl"
                  >
                    Reset Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredBlueprints.map((tt) => (
                  <BlueprintCard
                    key={tt.id}
                    tt={tt}
                    examId={exam.id}
                    onDuplicate={handleDuplicateBlueprint}
                    onDelete={handleDeleteBlueprint}
                    onToggleLeaderboard={handleToggleLeaderboard}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── TAB 2: TESTS ─── */}
        {activeTab === 'tests' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Filter and Action Header */}
            <div className="bg-card border border-border/80 rounded-2xl p-3.5 sm:p-4 shadow-glass-xs space-y-3">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-1.5 truncate">
                    <FileText className="h-4 w-4 text-secondary shrink-0" />
                    <span>Tests</span>
                  </h2>
                  <Badge variant="outline" className="text-[10px] font-bold bg-muted/60 text-muted-foreground border-border shrink-0 hidden sm:inline-flex">
                    {mapped_tests.length} Tests
                  </Badge>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {unmapped_tests.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setMapTestModalOpen(true)}
                      className="text-xs font-bold rounded-xl gap-1 h-8 px-2 sm:px-2.5 border-border/80"
                    >
                      <Link2 className="h-3.5 w-3.5 text-secondary" /> <span className="hidden sm:inline">Map Existing</span><span className="sm:hidden">Map</span>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setCreateTestModalOpen(true)}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold rounded-xl gap-1 h-8 px-2.5 sm:px-3 shadow-2xs"
                  >
                    <Plus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Create Test</span><span className="sm:hidden">Add</span>
                  </Button>
                </div>
              </div>

              {/* Search & Filter Toolbar */}
              {mapped_tests.length > 0 && (
                <div className="space-y-2.5 pt-2.5 border-t border-border/50">
                  <div className="relative w-full">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      type="text"
                      placeholder="Search tests by title or type..."
                      value={testSearch}
                      onChange={(e) => setTestSearch(e.target.value)}
                      className="pl-8 pr-8 h-8 rounded-xl bg-background border-border/80 text-xs"
                    />
                    {testSearch && (
                      <button
                        type="button"
                        onClick={() => setTestSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
                    <div className="flex items-center p-0.5 bg-muted/40 rounded-xl border border-border/60 shrink-0">
                      {(['all', 'published', 'draft', 'archived'] as const).map(status => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setTestStatusFilter(status)}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                            testStatusFilter === status
                              ? 'bg-card text-foreground shadow-2xs border border-border/60'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>

                    <div className="flex items-center p-0.5 bg-muted/40 rounded-xl border border-border/60 shrink-0">
                      {(['all', 'free', 'premium'] as const).map(p => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setTestPricingFilter(p)}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                            testPricingFilter === p
                              ? 'bg-card text-foreground shadow-2xs border border-border/60'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Test Cards Grid */}
            {filteredTests.length === 0 ? (
              <Card className="border-border/80 bg-card rounded-2xl">
                <CardContent className="py-12 text-center text-muted-foreground space-y-3">
                  <FileText className="h-10 w-10 mx-auto text-muted-foreground/50" />
                  <p className="font-bold text-base text-foreground">
                    {mapped_tests.length === 0 ? 'No tests associated with this exam yet.' : 'No tests matching current search filters.'}
                  </p>
                  <p className="text-xs max-w-sm mx-auto">
                    {mapped_tests.length === 0
                      ? 'Launch the AI Test Generator or create a test manually to assign tests to this exam.'
                      : 'Try resetting search keywords or filter pills to view available tests.'}
                  </p>
                  {mapped_tests.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setTestSearch('')
                        setTestStatusFilter('all')
                        setTestPricingFilter('all')
                      }}
                      className="text-xs rounded-xl"
                    >
                      Reset Filters
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredTests.map((t) => (
                  <Card key={t.id} className="flex flex-col justify-between border-border/80 bg-card hover:border-primary/40 transition-all rounded-2xl shadow-glass-xs overflow-hidden">
                    <CardHeader className="p-3.5 sm:p-5 pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-wider bg-muted/40">
                          {t.test_type.replace('_', ' ')}
                        </Badge>
                        <div className="flex items-center gap-1.5">
                          <Badge className={`text-[10px] font-extrabold uppercase px-2 py-0.2 rounded-md ${
                            t.status === 'published'
                              ? 'bg-primary/15 text-primary border border-primary/30'
                              : t.status === 'archived'
                              ? 'bg-muted text-muted-foreground border border-border'
                              : 'bg-secondary/15 text-secondary border border-secondary/30'
                          }`}>
                            {t.status}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] font-bold uppercase bg-muted/30">
                            {t.language?.toUpperCase() || 'EN'}
                          </Badge>
                        </div>
                      </div>

                      <CardTitle className="text-sm sm:text-base font-bold text-foreground mt-2 line-clamp-2 leading-snug">
                        {t.title}
                      </CardTitle>
                      {t.description && (
                        <CardDescription className="line-clamp-2 text-xs text-muted-foreground leading-relaxed mt-1">
                          {t.description}
                        </CardDescription>
                      )}
                    </CardHeader>

                    <CardContent className="p-3.5 sm:p-5 pt-0 space-y-3">
                      {/* Metric Strip */}
                      <div className="grid grid-cols-3 gap-1 bg-muted/40 p-2 rounded-xl border border-border/50 text-center text-xs">
                        <div>
                          <div className="text-[10px] text-muted-foreground font-semibold">Time</div>
                          <div className="font-extrabold text-foreground">{formatTestDuration(t.duration_minutes)}</div>
                        </div>
                        <div className="border-x border-border/50">
                          <div className="text-[10px] text-muted-foreground font-semibold">Questions</div>
                          <div className="font-extrabold text-foreground">{t.question_count} Qs</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground font-semibold">Pricing</div>
                          <div className={`font-extrabold ${t.is_free ? 'text-primary' : 'text-tertiary'}`}>
                            {t.is_free ? 'Free' : `${t.credit_cost} cr`}
                          </div>
                        </div>
                      </div>

                      {/* Card Action Row */}
                      <div className="flex items-center gap-2 pt-1">
                        <Button className="flex-1 h-8 text-xs font-bold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 min-w-0 px-2 sm:px-3" size="sm" asChild>
                          <Link href={`/admin/tests/${t.id}?return_to=/admin/exams/${exam.id}?tab=tests`} className="inline-flex items-center justify-center truncate gap-1.5">
                            <span className="hidden sm:inline">Manage Test</span>
                            <span className="sm:hidden">Manage</span>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-xl text-destructive hover:bg-destructive/10 border-border/80 shrink-0"
                          onClick={() => handleUnmapTest(t.id, t.title)}
                          title="Unmap Test from Exam"
                        >
                          <Unlink className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── TAB 3: CURRICULUM & SYLLABI ─── */}
        {activeTab === 'syllabi' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-card border border-border/80 rounded-2xl p-3.5 sm:p-4 shadow-glass-xs flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-1.5 truncate">
                  <BookOpen className="h-4 w-4 text-tertiary shrink-0" />
                  <span>Curriculum & Syllabi</span>
                </h2>
                <Badge variant="outline" className="text-[10px] font-bold bg-muted/60 text-muted-foreground border-border shrink-0 hidden sm:inline-flex">
                  {mapped_syllabi.length} Mapped
                </Badge>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                {unmapped_syllabi.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMapSyllabusModalOpen(true)}
                    className="text-xs font-bold rounded-xl gap-1 h-8 px-2 sm:px-2.5 border-border/80"
                  >
                    <Link2 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Map Existing</span><span className="sm:hidden">Map</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => setCreateSyllabusModalOpen(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold rounded-xl gap-1 h-8 px-2.5 sm:px-3 shadow-2xs"
                >
                  <Plus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Create Syllabus</span><span className="sm:hidden">Add</span>
                </Button>
              </div>
            </div>

            {mapped_syllabi.length === 0 ? (
              <Card className="border-border/80 bg-card rounded-2xl">
                <CardContent className="py-12 text-center text-muted-foreground space-y-3">
                  <BookOpen className="h-10 w-10 mx-auto text-muted-foreground/50" />
                  <p className="font-bold text-base text-foreground">No syllabus currently mapped to {exam.name}.</p>
                  <p className="text-xs max-w-sm mx-auto">
                    Map an existing prescribed syllabus to this exam or create a new syllabus to organize subjects and chapters.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => setCreateSyllabusModalOpen(true)}
                    className="text-xs font-bold rounded-xl gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" /> Create First Syllabus
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {mapped_syllabi.map((s) => (
                  <Card key={s.id} className="flex flex-col justify-between border-border/80 bg-card hover:border-primary/40 transition-all rounded-2xl shadow-glass-xs">
                    <CardHeader className="p-3.5 sm:p-5 pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-sm sm:text-base font-bold text-foreground flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-tertiary" />
                          <span>{s.name}</span>
                        </CardTitle>
                        <Badge variant="outline" className="text-xs font-bold bg-muted/40 font-mono">
                          v{s.version}
                        </Badge>
                      </div>
                      {s.description && (
                        <CardDescription className="line-clamp-2 text-xs text-muted-foreground mt-1 leading-relaxed">
                          {s.description}
                        </CardDescription>
                      )}
                    </CardHeader>

                    <CardContent className="p-3.5 sm:p-5 pt-0 space-y-3">
                      {/* Stats and Subjects preview */}
                      <div className="flex items-center gap-4 text-xs font-semibold text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <strong className="text-foreground">{s.subjects_count}</strong> Prescribed Subjects
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1.5">
                          <strong className="text-foreground">{s.chapters_count}</strong> Chapters
                        </span>
                      </div>

                      {/* Subject Chips Summary */}
                      {s.subjects && s.subjects.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {s.subjects.slice(0, 5).map(sub => (
                            <span
                              key={sub.id}
                              className="text-[11px] font-semibold px-2 py-0.5 rounded-lg bg-muted/60 border border-border/60 text-foreground"
                            >
                              {sub.name} <span className="text-muted-foreground text-[10px]">({sub.chapters.length})</span>
                            </span>
                          ))}
                          {s.subjects.length > 5 && (
                            <span className="text-[10px] font-bold text-muted-foreground px-1.5 py-0.5">
                              +{s.subjects.length - 5} more
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                        <Button className="flex-1 h-8 text-xs font-bold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 min-w-0 px-2 sm:px-3" size="sm" asChild>
                          <Link href={`/admin/syllabi/${s.id}?return_to=/admin/exams/${exam.id}?tab=syllabi`} className="inline-flex items-center justify-center truncate gap-1.5">
                            <span className="hidden sm:inline">Manage Syllabus Tree</span>
                            <span className="sm:hidden">Manage Tree</span>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-xl text-destructive hover:bg-destructive/10 border-border/80 shrink-0"
                          onClick={() => handleUnmapSyllabus(s.id, s.name)}
                          title="Unmap Syllabus from Exam"
                        >
                          <Unlink className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── TAB 4: MARKING SCHEMES ─── */}
        {activeTab === 'rules' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-card border border-border/80 rounded-2xl p-3.5 sm:p-4 shadow-glass-xs space-y-1">
              <div className="flex items-center gap-2">
                <h2 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-1.5">
                  <Award className="h-4 w-4 text-primary shrink-0" />
                  <span>Marking Schemes & Grading Rules</span>
                </h2>
                <Badge variant="outline" className="text-[10px] font-bold bg-muted/60 text-muted-foreground border-border">
                  {marking_rules.length} Rules
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                Define reusable grading schemes linked to {exam.name}.
              </p>
            </div>

            <MarkingRulesManager
              rules={marking_rules}
              onSaveRule={(rule) => {
                if (rule.id) {
                  router.patch(`/admin/exams/${exam.id}/exam_marking_rules/${rule.id}`, {
                    exam_marking_rule: rule as any
                  })
                } else {
                  router.post(`/admin/exams/${exam.id}/exam_marking_rules`, {
                    exam_marking_rule: rule as any
                  })
                }
              }}
              onDeleteRule={(ruleId) => {
                if (confirm('Delete this marking rule?')) {
                  router.delete(`/admin/exams/${exam.id}/exam_marking_rules/${ruleId}`)
                }
              }}
            />
          </div>
        )}

        {/* ─── TAB 5: CANDIDATE TOOLS & SETTINGS ─── */}
        {activeTab === 'settings' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-card border border-border/80 rounded-2xl p-3.5 sm:p-4 shadow-glass-xs space-y-1">
              <h2 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-1.5">
                <SlidersHorizontal className="h-4 w-4 text-primary shrink-0" />
                <span>Candidate Tools & Settings</span>
              </h2>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                Configure candidate-facing features, exam identity, and attempt environment settings for {exam.name}.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Candidate Test Experience Tools */}
              <Card className="border-border/80 bg-card shadow-glass-xs rounded-2xl">
                <CardHeader className="p-3.5 sm:p-5 border-b border-border/60">
                  <CardTitle className="text-sm sm:text-base font-bold flex items-center gap-2 text-foreground">
                    <Calculator className="h-4 w-4 text-primary" /> Candidate Test Environment Tools
                  </CardTitle>
                  <CardDescription className="text-xs text-muted-foreground">
                    Configure candidate-facing features available inside mock attempt interfaces for {exam.name}.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3.5 sm:p-5 space-y-4">
                  <form onSubmit={handleSaveSettings} className="space-y-4">
                    {/* On-Screen Calculator */}
                    <div className="flex items-start justify-between gap-4 p-3 rounded-xl border border-border/60 bg-muted/30">
                      <div className="space-y-0.5">
                        <Label htmlFor="calc-toggle" className="text-xs font-bold text-foreground cursor-pointer flex items-center gap-1.5">
                          <Calculator className="h-3.5 w-3.5 text-primary" /> On-Screen Scientific Calculator
                        </Label>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          When enabled, candidates see a floating scientific calculator tool on desktop test attempts.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id="calc-toggle"
                        checked={calculatorEnabled}
                        onChange={(e) => setCalculatorEnabled(e.target.checked)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer mt-0.5"
                      />
                    </div>

                    {/* Language Switch Toggle */}
                    <div className="flex items-start justify-between gap-4 p-3 rounded-xl border border-border/60 bg-muted/30">
                      <div className="space-y-0.5">
                        <Label htmlFor="lang-switch-toggle" className="text-xs font-bold text-foreground cursor-pointer flex items-center gap-1.5">
                          <Globe className="h-3.5 w-3.5 text-secondary" /> Dynamic Language Switching
                        </Label>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Allow candidates to dynamically toggle question translation between English and Hindi during the live attempt.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id="lang-switch-toggle"
                        checked={allowLanguageSwitch}
                        onChange={(e) => setAllowLanguageSwitch(e.target.checked)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer mt-0.5"
                      />
                    </div>

                    {/* Primary Language */}
                    <div className="space-y-1.5 p-3 rounded-xl border border-border/60 bg-muted/30">
                      <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                        <Globe className="h-3.5 w-3.5 text-primary" /> Default Examination Language
                      </Label>
                      <select
                        value={primaryLanguage}
                        onChange={(e) => setPrimaryLanguage(e.target.value)}
                        className="flex h-9 w-full rounded-xl border border-border bg-background px-3 py-1.5 text-xs text-foreground font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="en">English (en)</option>
                        <option value="hi">Hindi (hi)</option>
                      </select>
                    </div>

                    {/* Score Display Mode */}
                    <div className="space-y-1.5 p-3 rounded-xl border border-border/60 bg-muted/30">
                      <div className="space-y-0.5">
                        <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                          <Award className="h-3.5 w-3.5 text-primary" /> Score Reporting & Leaderboard Ranking Mode
                        </Label>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Determines the primary score metric displayed on student result cards and how candidate rankings are ordered on nationwide leaderboards.
                        </p>
                      </div>
                      <select
                        value={scoreDisplayMode}
                        onChange={(e) => setScoreDisplayMode(e.target.value)}
                        className="flex h-9 w-full rounded-xl border border-border bg-background px-3 py-1.5 text-xs text-foreground font-semibold focus:outline-none focus:ring-1 focus:ring-primary mt-1"
                      >
                        <option value="raw_score">Raw Score (e.g. 450 / 720 pts) — NEET / General Mock Tests</option>
                        <option value="percentage">Percentage (e.g. 68.0%) — NISM / SBI / Banking Certifications</option>
                        <option value="percentile">Percentile (e.g. 97.4 percentile) — MH-CET / CAT</option>
                        <option value="nta_score">NTA Score (e.g. NTA Score: 97.4) — JEE Main NTA Standard</option>
                      </select>
                      <div className="pt-1 text-[10px] text-muted-foreground font-medium">
                        {scoreDisplayMode === 'percentage' && 'Leaderboard ranks candidates by best percentage score across eligible blueprints.'}
                        {scoreDisplayMode === 'percentile' && 'Leaderboard ranks candidates by best per-test percentile relative to all participants.'}
                        {scoreDisplayMode === 'nta_score' && 'Leaderboard ranks candidates by normalized NTA percentile score.'}
                        {scoreDisplayMode === 'raw_score' && 'Leaderboard ranks candidates by best total marks.'}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSavingSettings}
                      className="w-full h-9 text-xs font-bold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs"
                    >
                      {isSavingSettings ? 'Saving Settings...' : 'Save Candidate Environment Settings'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Exam Metadata & Active Status */}
              <Card className="border-border/80 bg-card shadow-glass-xs rounded-2xl">
                <CardHeader className="p-3.5 sm:p-5 border-b border-border/60">
                  <CardTitle className="text-sm sm:text-base font-bold flex items-center gap-2 text-foreground">
                    <ShieldCheck className="h-4 w-4 text-primary" /> Exam Identity & Publishing
                  </CardTitle>
                  <CardDescription className="text-xs text-muted-foreground">
                    Core exam identification details, publishing status, and visibility.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3.5 sm:p-5 space-y-4">
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-xl border border-border/60 bg-muted/30">
                      <span className="text-xs font-semibold text-muted-foreground">Exam Name</span>
                      <span className="text-xs font-bold text-foreground">{exam.name}</span>
                    </div>

                    <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-xl border border-border/60 bg-muted/30">
                      <span className="text-xs font-semibold text-muted-foreground">Exam Code</span>
                      <span className="text-xs font-mono font-bold text-foreground">{exam.code}</span>
                    </div>

                    <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-xl border border-border/60 bg-muted/30">
                      <span className="text-xs font-semibold text-muted-foreground">Exam Year</span>
                      <span className="text-xs font-bold text-foreground">{exam.year || 'None (All-Year)'}</span>
                    </div>

                    <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-xl border border-border/60 bg-muted/30">
                      <span className="text-xs font-semibold text-muted-foreground">Score Reporting Mode</span>
                      <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/20 capitalize">
                        {scoreDisplayMode ? scoreDisplayMode.replace(/_/g, ' ') : 'Raw Score'}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-xl border border-border/60 bg-muted/30">
                      <span className="text-xs font-semibold text-muted-foreground">Active Status</span>
                      <Badge className={`text-[10px] font-bold ${
                        exam.is_active
                          ? 'bg-primary/15 text-primary border border-primary/30'
                          : 'bg-muted text-muted-foreground border border-border'
                      }`}>
                        {exam.is_active ? 'Active Exam' : 'Inactive / Draft'}
                      </Badge>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditExamModalOpen(true)}
                    className="w-full h-9 text-xs font-bold rounded-xl border-border/80 hover:bg-muted gap-1.5"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit Exam Identity Details
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ─── MODALS ─── */}

        {/* Edit Exam Dialog */}
        <Dialog open={editExamModalOpen} onOpenChange={setEditExamModalOpen}>
          <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6 rounded-2xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-foreground">Edit Exam Details</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Update exam name, code, year, and publishing status.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateExam} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Exam Name *</label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="rounded-xl border-border text-xs h-9"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Code *</label>
                  <Input
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                    className="rounded-xl border-border text-xs h-9 font-mono uppercase"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Year</label>
                  <Input
                    type="number"
                    value={editYear}
                    onChange={(e) => setEditYear(e.target.value ? Number(e.target.value) : '')}
                    className="rounded-xl border-border text-xs h-9"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Score Reporting Mode</label>
                <select
                  value={scoreDisplayMode}
                  onChange={(e) => setScoreDisplayMode(e.target.value)}
                  className="flex h-9 w-full rounded-xl border border-border bg-background px-3 py-1.5 text-xs text-foreground font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="raw_score">Raw Score (e.g. 450 / 720 pts)</option>
                  <option value="percentage">Percentage (e.g. 68.0%)</option>
                  <option value="percentile">Percentile (e.g. 97.4 percentile)</option>
                  <option value="nta_score">NTA Score (e.g. NTA Score: 97.4)</option>
                </select>
              </div>

              <div className="space-y-2 pt-1 border-t border-border/40">
                <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>Active Exam (Visible to Candidates)</span>
                </label>
              </div>

              <DialogFooter className="grid grid-cols-2 gap-2.5 pt-2 sm:flex sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setEditExamModalOpen(false)} className="rounded-xl text-xs h-9 border-border/80">
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-xs font-bold h-9 shadow-2xs">
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Map Existing Syllabus Dialog */}
        <Dialog open={mapSyllabusModalOpen} onOpenChange={setMapSyllabusModalOpen}>
          <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6 rounded-2xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-foreground">Map Existing Syllabus</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Select an existing prescribed syllabus to link to {exam.name}.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleMapSyllabus} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Select Syllabus *</label>
                <select
                  className="flex h-10 w-full rounded-xl border border-border bg-background px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={selectedSyllabusId}
                  onChange={(e) => setSelectedSyllabusId(Number(e.target.value))}
                  required
                >
                  <option value="" disabled>-- Select Syllabus --</option>
                  {unmapped_syllabi.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} (v{s.version}){s.current_exam_name ? ` — Currently on ${s.current_exam_name}` : ' — Unmapped'}
                    </option>
                  ))}
                </select>
              </div>

              <DialogFooter className="grid grid-cols-2 gap-2.5 pt-2 sm:flex sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setMapSyllabusModalOpen(false)} className="rounded-xl text-xs h-9 border-border/80">
                  Cancel
                </Button>
                <Button type="submit" disabled={!selectedSyllabusId} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-xs font-bold h-9 shadow-2xs">
                  Map Syllabus
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create New Syllabus Dialog */}
        <Dialog open={createSyllabusModalOpen} onOpenChange={setCreateSyllabusModalOpen}>
          <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6 rounded-2xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-foreground">Create New Syllabus</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Create a new syllabus tree pre-linked to {exam.name}.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSyllabus} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Syllabus Title *</label>
                <Input
                  value={newSyllabusName}
                  onChange={(e) => setNewSyllabusName(e.target.value)}
                  className="rounded-xl border-border text-xs h-9"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Version</label>
                <Input
                  type="number"
                  value={newSyllabusVersion}
                  onChange={(e) => setNewSyllabusVersion(Number(e.target.value) || 1)}
                  className="rounded-xl border-border text-xs h-9"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Description (Optional)</label>
                <textarea
                  className="flex min-h-[70px] w-full rounded-xl border border-border bg-background px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Prescribed curriculum details..."
                  value={newSyllabusDesc}
                  onChange={(e) => setNewSyllabusDesc(e.target.value)}
                />
              </div>

              <DialogFooter className="grid grid-cols-2 gap-2.5 pt-2 sm:flex sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setCreateSyllabusModalOpen(false)} className="rounded-xl text-xs h-9 border-border/80">
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-xs font-bold h-9 shadow-2xs">
                  Create Syllabus
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Map Existing Test Dialog */}
        <Dialog open={mapTestModalOpen} onOpenChange={setMapTestModalOpen}>
          <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6 rounded-2xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-foreground">Map Existing Test</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Select an unmapped or unassigned mock test to link to {exam.name}.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleMapTest} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Select Test *</label>
                <select
                  className="flex h-10 w-full rounded-xl border border-border bg-background px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={selectedTestId}
                  onChange={(e) => setSelectedTestId(Number(e.target.value))}
                  required
                >
                  <option value="" disabled>-- Select Test --</option>
                  {unmapped_tests.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.title} ({t.test_type.replace('_', ' ')}{t.current_exam_name ? ` — on ${t.current_exam_name}` : ' — Unmapped'})
                    </option>
                  ))}
                </select>
              </div>

              <DialogFooter className="grid grid-cols-2 gap-2.5 pt-2 sm:flex sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setMapTestModalOpen(false)} className="rounded-xl text-xs h-9 border-border/80">
                  Cancel
                </Button>
                <Button type="submit" disabled={!selectedTestId} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-xs font-bold h-9 shadow-2xs">
                  Map Test
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Manual Test Dialog */}
        <Dialog open={createTestModalOpen} onOpenChange={setCreateTestModalOpen}>
          <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6 rounded-2xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-foreground">Create Test for {exam.name}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Create a new mock test directly associated with this exam.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTest} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Test Title *</label>
                <Input
                  value={newTestTitle}
                  onChange={(e) => setNewTestTitle(e.target.value)}
                  className="rounded-xl border-border text-xs h-9"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Test Type</label>
                  <select
                    className="flex h-9 w-full rounded-xl border border-border bg-background px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={newTestType}
                    onChange={(e) => setNewTestType(e.target.value)}
                  >
                    <option value="full_mock">Full Mock</option>
                    <option value="chapter_test">Chapter Test</option>
                    <option value="subject_test">Subject Test</option>
                    <option value="pyq_paper">PYQ Paper</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Language</label>
                  <select
                    className="flex h-9 w-full rounded-xl border border-border bg-background px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={newTestLanguage}
                    onChange={(e) => setNewTestLanguage(e.target.value)}
                  >
                    <option value="en">English (en)</option>
                    <option value="hi">Hindi (hi)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Duration (minutes)</label>
                <Input
                  type="number"
                  min="5"
                  max="600"
                  value={newTestDuration}
                  onChange={(e) => setNewTestDuration(Number(e.target.value) || 180)}
                  className="rounded-xl border-border text-xs h-9"
                  required
                />
              </div>

              <div className="space-y-3 pt-1 border-t border-border/40">
                <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTestIsFree}
                    onChange={(e) => setNewTestIsFree(e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>Free Access Test</span>
                </label>

                {!newTestIsFree && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Credit Cost *</label>
                    <Input
                      type="number"
                      min="1"
                      value={newTestCreditCost}
                      onChange={(e) => setNewTestCreditCost(Number(e.target.value) || 1)}
                      className="rounded-xl border-border text-xs h-9"
                      required
                    />
                  </div>
                )}
              </div>

              <DialogFooter className="grid grid-cols-2 gap-2.5 pt-2 sm:flex sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setCreateTestModalOpen(false)} className="rounded-xl text-xs h-9 border-border/80">
                  Cancel
                </Button>
                <Button type="submit" disabled={!newTestTitle.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-xs font-bold h-9 shadow-2xs">
                  Create Test
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  )
}
